import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Turnstile } from '@marsidev/react-turnstile';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiLoader, FiArrowLeft, FiClock } = FiIcons;

const LOCKOUT_STORAGE_KEY = 'sohlocal_auth_lockout';
const ATTEMPT_STORAGE_KEY = 'sohlocal_auth_attempts';
const MAX_ATTEMPTS = 5;
const BASE_COOLDOWN = 30000; // 30 seconds

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn, loading: authLoading } = useSupabaseAuth();
  
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);

  const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const fromPath = location.state?.from || '/';
  const isFromListing = fromPath.includes('/seller/');

  // Initialize lockout state from storage
  useEffect(() => {
    const savedLockout = localStorage.getItem(LOCKOUT_STORAGE_KEY);
    if (savedLockout) {
      const remaining = parseInt(savedLockout) - Date.now();
      if (remaining > 0) {
        setLockoutTime(parseInt(savedLockout));
        setTimeLeft(Math.ceil(remaining / 1000));
      } else {
        localStorage.removeItem(LOCKOUT_STORAGE_KEY);
      }
    }
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      const remaining = lockoutTime - Date.now();
      if (remaining <= 0) {
        setLockoutTime(0);
        setTimeLeft(0);
        localStorage.removeItem(LOCKOUT_STORAGE_KEY);
        clearInterval(timer);
      } else {
        setTimeLeft(Math.ceil(remaining / 1000));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, lockoutTime]);

  useEffect(() => {
    if (user && !authLoading) {
      navigate(fromPath, { replace: true });
    }
  }, [user, authLoading, navigate, fromPath]);

  const handleFailedAttempt = useCallback(() => {
    const currentAttempts = parseInt(localStorage.getItem(ATTEMPT_STORAGE_KEY) || '0') + 1;
    localStorage.setItem(ATTEMPT_STORAGE_KEY, currentAttempts.toString());

    if (currentAttempts >= MAX_ATTEMPTS) {
      // Exponential backoff: 30s, 60s, 120s...
      const multiplier = Math.pow(2, currentAttempts - MAX_ATTEMPTS);
      const cooldown = BASE_COOLDOWN * multiplier;
      const expiry = Date.now() + cooldown;
      
      setLockoutTime(expiry);
      setTimeLeft(Math.ceil(cooldown / 1000));
      localStorage.setItem(LOCKOUT_STORAGE_KEY, expiry.toString());
      setError(`Too many failed attempts. Please wait ${Math.ceil(cooldown / 1000)} seconds.`);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (timeLeft > 0) return;
    if (!turnstileToken) {
      setError('Please verify that you are human.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { error: signInError } = await signIn(formData.email, formData.password, turnstileToken);
      if (signInError) throw signInError;
      
      // Clear attempts on success
      localStorage.removeItem(ATTEMPT_STORAGE_KEY);
      localStorage.removeItem(LOCKOUT_STORAGE_KEY);
    } catch (err) {
      console.error('Login error:', err);
      handleFailedAttempt();
      
      // Obfuscate specific error messages to prevent account enumeration
      if (err.message?.toLowerCase().includes('invalid login credentials')) {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(err.message || 'An error occurred during sign in.');
      }
    } finally {
      setIsSubmitting(false);
      setTurnstileToken(null);
      setTurnstileKey((currentKey) => currentKey + 1);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="max-w-md w-full space-y-8 bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl shadow-slate-200/50 border border-slate-100"
      >
        <div className="text-center">
          <Link to={fromPath} className="inline-flex items-center text-[10px] font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest mb-8 transition-colors group">
            <SafeIcon icon={FiArrowLeft} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {isFromListing ? 'Return to Listing' : 'Back to Marketplace'}
          </Link>
          <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center p-4 mx-auto mb-6 shadow-xl shadow-slate-200">
            <img src="https://assets.sohlocal.com/Branding/Icon.png" alt="SohLocal Icon" className="w-full h-full object-contain" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 tracking-tight">Welcome Back</h2>
          <p className="mt-2 text-sm text-slate-500 font-medium">Sign in to your secure SohLocal account</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className={`px-4 py-3 rounded-2xl flex items-start text-sm border ${timeLeft > 0 ? 'bg-amber-50 border-amber-100 text-amber-700' : 'bg-red-50 border-red-100 text-red-700'}`}>
              <SafeIcon icon={timeLeft > 0 ? FiClock : FiAlertCircle} className="mt-0.5 mr-2 flex-shrink-0" />
              <span className="font-bold">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Email Address</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SafeIcon icon={FiMail} className="text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input 
                  name="email" 
                  type="email" 
                  required 
                  disabled={timeLeft > 0}
                  value={formData.email} 
                  onChange={handleChange} 
                  className="pl-11 block w-full px-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-medium text-slate-900 disabled:opacity-50" 
                  placeholder="name@example.com" 
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 ml-1">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Password</label>
                <Link to="/forgot-password" size="sm" className="text-[10px] font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-widest transition-colors">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <SafeIcon icon={FiLock} className="text-slate-300 group-focus-within:text-indigo-600 transition-colors" />
                </div>
                <input 
                  name="password" 
                  type={showPassword ? 'text' : 'password'} 
                  required 
                  disabled={timeLeft > 0}
                  value={formData.password} 
                  onChange={handleChange} 
                  className="pl-11 pr-12 block w-full px-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all font-medium text-slate-900 disabled:opacity-50" 
                  placeholder="••••••••" 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-slate-300 hover:text-indigo-600 transition-colors" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-center py-2">
            <Turnstile key={turnstileKey} siteKey={SITE_KEY} onSuccess={(token) => { setTurnstileToken(token); setError(''); }} onExpire={() => setTurnstileToken(null)} />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting || authLoading || !turnstileToken || timeLeft > 0} 
            className="w-full flex justify-center py-4 px-4 bg-indigo-600 text-white text-sm font-black rounded-2xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-xl shadow-indigo-100 transform active:scale-[0.98]"
          >
            {isSubmitting ? (
              <SafeIcon icon={FiLoader} className="animate-spin mr-2" />
            ) : timeLeft > 0 ? (
              `Locked (${timeLeft}s)`
            ) : (
              'Sign In'
            )}
          </button>

          <div className="text-center pt-2">
            <span className="text-xs text-slate-500 font-bold uppercase tracking-widest">
              New to SohLocal?{' '}
              <Link to="/register" state={{ from: fromPath }} className="ml-1 text-indigo-600 hover:text-indigo-700 transition-colors">Create Account</Link>
            </span>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LoginPage;
